/**
 * Trustpilot Service
 * Manages global Trustpilot script loading and widget initialization
 */

let isScriptLoaded = false;
let isScriptLoading = false;
let scriptCallbacks = [];

/**
 * Load Trustpilot script once globally
 * Subsequent calls will reuse the same script
 */
export const loadTrustpilotScript = () => {
  return new Promise((resolve, reject) => {
    // If already loaded, resolve immediately
    if (isScriptLoaded && window.Trustpilot) {
      resolve(window.Trustpilot);
      return;
    }

    // If currently loading, queue the callback
    if (isScriptLoading) {
      scriptCallbacks.push({ resolve, reject });
      return;
    }

    // Check if script already exists in DOM
    const existingScript = document.getElementById('trustpilot-script');
    if (existingScript && window.Trustpilot) {
      isScriptLoaded = true;
      resolve(window.Trustpilot);
      return;
    }

    // Start loading
    isScriptLoading = true;

    const script = document.createElement('script');
    script.id = 'trustpilot-script';
    script.src = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
    script.async = true;

    script.onload = () => {
      isScriptLoaded = true;
      isScriptLoading = false;
      
      // Resolve main promise
      resolve(window.Trustpilot);
      
      // Resolve queued callbacks
      scriptCallbacks.forEach(cb => cb.resolve(window.Trustpilot));
      scriptCallbacks = [];
    };

    script.onerror = (error) => {
      isScriptLoading = false;
      
      // Reject main promise
      reject(error);
      
      // Reject queued callbacks
      scriptCallbacks.forEach(cb => cb.reject(error));
      scriptCallbacks = [];
    };

    document.body.appendChild(script);
  });
};

/**
 * Load Trustpilot widget for a specific element
 * @param {HTMLElement|string} element - Element or selector
 * @param {boolean} force - Force reload
 */
export const loadTrustpilotWidget = async (element, force = false) => {
  try {
    const Trustpilot = await loadTrustpilotScript();
    
    if (Trustpilot && Trustpilot.loadFromElement) {
      const targetElement = typeof element === 'string' 
        ? document.querySelector(element) 
        : element;
      
      if (targetElement) {
        Trustpilot.loadFromElement(targetElement, force);
      }
    }
  } catch (error) {
    console.error('Failed to load Trustpilot widget:', error);
  }
};

/**
 * Initialize Trustpilot script on app load
 * Call this once in _app.jsx
 */
export const initTrustpilot = () => {
  if (typeof window === 'undefined') return;
  
  // Load script with delay to not impact initial page load
  // Trustpilot is not critical for first paint
  const delay = 3000; // 3 seconds after page load
  
  setTimeout(() => {
    loadTrustpilotScript().catch(error => {
      console.error('Failed to initialize Trustpilot:', error);
    });
  }, delay);
};

